import {
  type AnalysisType,
  type AnalysisTypes,
  type CreateAnalysisType,
  type CreateAnalysisTypeSubstance,
  type CreateSampleType,
  type SampleType,
  type SampleTypes,
  useCreateAnalysisTypeMutation,
  useCreateSampleTypeMutation
} from 'prove-common'
import { useMutation, useQueryClient } from '@tanstack/react-query'

interface AnalysisTypeMutationVariables {
  selectedProductId: number
  selectedMatrixId: number
  selectedSubstanceIds: number[]
  selectedPayloadSchemaId: number
}

export const filterSampleTypes = (
  sampleTypes: SampleTypes | undefined,
  productId: number,
  matrixId: number
) => {
  return sampleTypes?.filter((sampleType) => {
    const sameProduct = sampleType.productId === productId
    const sameMatrix = sampleType.matrixId === matrixId
    return sameProduct && sameMatrix
  })
}

export const useExistingOrCreateAnalysisType = (
  existingSampleTypes?: SampleTypes,
  existingAnalysisTypes?: AnalysisTypes
) => {
  const queryClient = useQueryClient()
  const createSampleType = useCreateSampleTypeMutation()
  const createAnalysisType = useCreateAnalysisTypeMutation()
  return useMutation({
    mutationFn: async ({
      selectedProductId,
      selectedMatrixId,
      selectedSubstanceIds,
      selectedPayloadSchemaId
    }: AnalysisTypeMutationVariables) => {
      // 1. Use existing sample type or create new
      let sampleType: SampleType | undefined = filterSampleTypes(
        existingSampleTypes,
        selectedProductId,
        selectedMatrixId
      )?.[0]
      if (sampleType == null) {
        const sampleTypeNew: CreateSampleType = {
          productId: selectedProductId,
          matrixId: selectedMatrixId
        }
        sampleType = await createSampleType.mutateAsync(sampleTypeNew)
      }
      if (sampleType == null) {
        throw new Error('Kunne ikke opprette prøvetype')
      }
      // 2. Use existing analysis type or create new
      let analysisType: AnalysisType | undefined = existingAnalysisTypes?.find(
        (analysisType) => {
          if (
            analysisType.sampleType != null &&
            analysisType.substances != null
          ) {
            const analysisTypeSubstanceIds = analysisType.substances
              .map((substance) => substance.substance.id)
              .toSorted((a, b) => a - b)
            const formDataSubstanceIds = selectedSubstanceIds.toSorted(
              (a, b) => a - b
            )
            const sameSampleType = analysisType.sampleType.id === sampleType?.id
            const sameSubstances =
              analysisTypeSubstanceIds.toString() ===
              formDataSubstanceIds.toString()
            const sameSchema =
              analysisType.payloadSchema?.id === selectedPayloadSchemaId
            return sameSampleType && sameSubstances && sameSchema
          } else {
            return false
          }
        }
      )
      if (analysisType == null) {
        const createAnalysisTypeSubstances: CreateAnalysisTypeSubstance[] =
          selectedSubstanceIds?.map((id) => {
            const createAnalysisTypeSubstance: CreateAnalysisTypeSubstance = {
              substanceId: id
            }
            return createAnalysisTypeSubstance
          })
        const analysisTypeNew: CreateAnalysisType = {
          sampleTypeId: sampleType.id,
          substances: createAnalysisTypeSubstances,
          payloadSchemaId: selectedPayloadSchemaId
        }
        analysisType = await createAnalysisType.mutateAsync(analysisTypeNew)
        await queryClient.invalidateQueries({
          queryKey: ['batch']
        })
      }
      return analysisType
    }
  })
}
